<template>
  <Modal v-model="visible" :canClose="true" class="modal modal-lg" :title="$t('orders.preview_modal.title')">
    <template v-if="isLoading" #body>
      <div class="d-flex justify-content-center align-items-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </template>
    <template v-else-if="!isLoading && hasOptions && !isSuccessState" #body>
      <div class="text-muted mb-3" style="text-align: left">
        {{ $t('orders.preview_modal.share_instruction') }}
      </div>

      <div class="preview-options mb-3">
        <h4>{{ $t('orders.preview_modal.ad_previews') }}</h4>
        <div class="preview-option-row" v-for="(optionProvider, providerKey) in options" :key="providerKey">
          <label class="form-selectgroup-item preview-option-provider">
            <input
              type="checkbox"
              :name="`preview-${providerKey}`"
              :value="providerKey"
              class="form-selectgroup-input"
              :checked="isProviderChecked(providerKey)"
              @change="toggleProvider(providerKey, optionProvider.previews, $event)" />
            <span class="form-selectgroup-label d-flex align-items-center">
              <component :is="getPreviewIcon(String(providerKey))" :size="18" stroke-width="1" class="me-2" />
              {{ optionProvider.name }}
            </span>
          </label>
          <div class="preview-option-items">
            <label
              v-for="preview in optionProvider.previews"
              :key="preview.key"
              class="form-selectgroup-item preview-option-item">
              <input
                type="checkbox"
                :name="`preview-${preview.key}`"
                :value="preview.key"
                class="form-selectgroup-input"
                :checked="isPreviewChecked(providerKey, preview.key)"
                @change="togglePreview(providerKey, preview.key, $event)" />
              <span class="form-selectgroup-label d-flex align-items-center"> {{ preview.name }} </span>
            </label>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="!isLoading && hasOptions && isSuccessState" #body>
      <div class="modal-body text-center py-4">
        <IconCircleCheck :size="48" stroke-width="1.5" class="icon mb-2 text-green icon-lg" />
        <h3>{{ $t('orders.preview_modal.preview_link_generated') }}</h3>
        <div class="text-secondary mb-4">
          {{ $t('orders.oreview_modal.generated_link_instruction') }}
        </div>
        <div class="d-flex align-items-center justify-content-between gap-2 w-100">
          <div style="flex-basis: 85%; flex-grow: 1">
            <FormInput v-model="previewLink" type="text" name="previewLink" :disabled="true" :readonly="true" />
          </div>
          <button class="btn btn-outline-primary" :disabled="!previewLink" @click="openPreviewLink">
            <i class="ti ti-external-link m-0"></i>
          </button>
        </div>
      </div>
    </template>
    <template v-else #body>
      <div class="alert alert-info" role="alert">
        {{ $t('orders.preview_modal.no_previews') }}
      </div>
    </template>

    <template #footer>
      <LoadingButton
        v-if="hasOptions && !isSuccessState"
        :disabled="Object.values(checkedOptions).length === 0"
        class="btn btn-primary"
        @press="handleSubmit">
        <IconShare :size="18" stroke-width="1.5" class="me-2" />
        {{ $t('btn.generate_link') }}
      </LoadingButton>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, inject } from 'vue';
import { FormInput, Modal, LoadingButton } from '@prospective/lithium';
import { fetchAvailablePreviews, savePreviewOptions } from '@/api/order';
import { useToast } from 'vue-toastification';
import type { Dispatcher } from '@/events/dispatcher';
import type { PreviewModalOpenEvent } from '@/events/modalEvents';
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandGoogle,
  IconBrandTiktok,
  IconAd2,
  IconEye,
  IconShare,
  IconCircleCheck,
  IconBrandLinkedin,
} from '@tabler/icons-vue';
import type { OrderPreview } from '@/types/types';

type PreviewProvider = { name: string; previews: Record<string, OrderPreview> };

type CheckedOptions = Record<string, string[]>;

const dispatcher = inject<Dispatcher>('dispatcher');
const toast = useToast();

const visible = ref(false);
const isLoading = ref(false);
const options = ref<Record<string, PreviewProvider>>({});
const previewLink = ref<string>('');
const checkedOptions = ref<CheckedOptions>({});
const currentOrderId = ref<number | null>(null);

function show(event: PreviewModalOpenEvent) {
  const { orderId } = event.payload;

  if (!orderId) {
    toast.error('Error: No order ID provided!');
    console.error('Order ID is null or undefined');
    return;
  }

  currentOrderId.value = orderId;
  visible.value = true;

  // Reset state
  options.value = {};
  previewLink.value = '';
  checkedOptions.value = {};

  // Fetch previews
  fetchPreviews(orderId);
}

const fetchPreviews = async (orderId: number) => {
  isLoading.value = true;
  try {
    const res = await fetchAvailablePreviews(orderId);
    if (res) {
      options.value = res;
    }
  } catch (error) {
    toast.error('Error fetching previews!');
    console.error('Error fetching previews:', error);
  } finally {
    isLoading.value = false;
  }
};

const isPreviewChecked = (providerKey: string, previewKey: string) => {
  return checkedOptions.value[providerKey]?.includes(previewKey) ?? false;
};

const isProviderChecked = (providerKey: string) => {
  const selectedPreviews = checkedOptions.value[providerKey] ?? [];

  return !!selectedPreviews.length;
};

const setProviderSelections = (providerKey: string, previewKeys: string[]) => {
  if (previewKeys.length === 0) {
    const remainingSelections = { ...checkedOptions.value };
    delete remainingSelections[providerKey];
    checkedOptions.value = remainingSelections;
    return;
  }

  checkedOptions.value = {
    ...checkedOptions.value,
    [providerKey]: previewKeys,
  };
};

const toggleProvider = (providerKey: string, providerPreviews: Record<string, OrderPreview>, event: Event) => {
  const shouldSelectAll = (event.target as HTMLInputElement).checked;
  const selectedPreviewKeys = shouldSelectAll ? Object.keys(providerPreviews) : [];

  setProviderSelections(providerKey, selectedPreviewKeys);
};

const togglePreview = (providerKey: string, previewKey: string, event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked;
  const currentSelections = checkedOptions.value[providerKey] ?? [];
  const nextSelections = isChecked
    ? [...currentSelections, previewKey]
    : currentSelections.filter((selectedKey) => selectedKey !== previewKey);

  setProviderSelections(providerKey, nextSelections);
};

const handleSubmit = async (stopLoading: any) => {
  if (!currentOrderId.value) {
    toast.error('Error generating preview link!');
    console.error('Order ID is null or undefined');
    return;
  }
  try {
    const res = await savePreviewOptions(currentOrderId.value, checkedOptions.value);

    if (res.url) {
      previewLink.value = res.url;
      toast.success('Preview link generated successfully!');
    } else {
      toast.error('Error generating preview link!');
    }
  } catch (error) {
    toast.error('Error generating preview link!');
    console.error('Error generating preview link:', error);
  } finally {
    stopLoading();
  }
};

const openPreviewLink = () => {
  if (previewLink.value) {
    window.open(previewLink.value, '_blank');
  }
};

const getPreviewIcon = (previewKey: string) => {
  // Map preview keys to appropriate Tabler icon components
  if (previewKey.includes('facebook')) {
    return IconBrandFacebook;
  } else if (previewKey.includes('instagram')) {
    return IconBrandInstagram;
  } else if (previewKey.includes('google')) {
    return IconBrandGoogle;
  } else if (previewKey.includes('adform') || previewKey.includes('display')) {
    return IconAd2;
  } else if (previewKey.includes('linkedin')) {
    return IconBrandLinkedin;
  } else if (previewKey.includes('tiktok')) {
    return IconBrandTiktok;
  } else {
    return IconEye; // Default icon for unknown types
  }
};

const hasOptions = computed(() => {
  return options.value && Object.values(options.value).length > 0;
});

const isSuccessState = computed(() => {
  return previewLink.value && previewLink.value.trim().length > 0;
});

onBeforeMount(() => {
  dispatcher?.connect<PreviewModalOpenEvent>('previewModalOpen', show);
});
</script>
<style scoped>
.preview-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.preview-option-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.75rem;
  border: 1px solid var(--tblr-border-color);
  border-radius: 0.5rem;
}

.preview-option-provider {
  flex: 0 0 auto;
}

.preview-option-provider .form-selectgroup-label {
  min-width: 10rem;
  font-weight: 600;
  color: var(--tblr-body-color);
}

.preview-option-items {
  display: flex;
  flex: 1 1 0;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: min(100%, 20rem);
}

.preview-option-item {
  flex: 0 0 auto;
}

.form-selectgroup-input:not(:checked) + .form-selectgroup-label {
  color: #8e939d;
  box-shadow: none;
  border-color: var(--tblr-border-color);
}
</style>
